<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  keyLabel: {
    type: String,
    required: true,
  },
  valueLabel: {
    type: String,
    required: true,
  },
  removeAriaLabel: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const rows = computed(() => {
  const o = props.modelValue || {};
  const entries = Object.entries(o);
  const mapped = entries.map(([key, value]) => ({
    key,
    value: String(value ?? ''),
  }));
  return mapped.length
    ? [...mapped, { key: '', value: '' }]
    : [{ key: '', value: '' }];
});

const commitFromRowList = rowList => {
  const o = {};
  rowList.forEach(r => {
    const k = String(r.key ?? '').trim();
    if (k) o[k] = String(r.value ?? '');
  });
  emit('update:modelValue', o);
};

const syncRow = (index, field, value) => {
  const next = rows.value.map((r, i) =>
    i === index ? { ...r, [field]: value } : { ...r }
  );
  commitFromRowList(next);
};

const removeRow = index => {
  const next = rows.value.filter((_, i) => i !== index);
  commitFromRowList(next);
};
</script>

<template>
  <div
    class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 dark:bg-n-alpha-2"
  >
    <p v-if="title" class="mb-1 text-heading-3 text-n-slate-12">
      {{ title }}
    </p>
    <p v-if="description" class="mb-3 text-xs text-n-slate-11">
      {{ description }}
    </p>
    <div class="space-y-3">
      <div
        v-for="(row, index) in rows"
        :key="index"
        class="flex flex-wrap items-end gap-2 rounded-lg bg-n-background p-2 outline outline-1 outline-n-weak dark:bg-n-solid-1"
      >
        <div class="min-w-[6rem] flex-1">
          <Input
            :model-value="row.key"
            :label="keyLabel"
            size="sm"
            @update:model-value="syncRow(index, 'key', $event)"
          />
        </div>
        <div class="min-w-[6rem] flex-1">
          <Input
            :model-value="row.value"
            :label="valueLabel"
            size="sm"
            @update:model-value="syncRow(index, 'value', $event)"
          />
        </div>
        <Button
          v-if="rows.length > 1"
          faded
          ruby
          size="sm"
          icon="i-lucide-trash-2"
          :aria-label="removeAriaLabel"
          @click="removeRow(index)"
        />
      </div>
    </div>
  </div>
</template>
